/** Rules of the WhatsApp credits v2 UI (toggle `enable_whatsapp_credits_v2`). */

export const CREDITS_V2_WARNING_USED_PERCENT = 75;
export const CREDITS_V2_DANGER_USED_PERCENT = 90;
/** Below this many credits left a company is flagged even if it used little. */
export const CREDITS_V2_LOW_REMAINING = 50;
/**
 * Below one credit nothing can be sent anymore (generic-bot blocks a send it can't
 * fully pay for), so a fractional balance like 0.92 already counts as empty.
 */
export const CREDITS_V2_EMPTY_BELOW = 1;

/** 0 = healthy, 1 = warning, 2 = critical, 3 = empty (same scale as v1). */
export type CreditsV2Level = 0 | 1 | 2 | 3;

export function creditsV2Level({
  total,
  percentageUsed,
  totalRemaining,
}: {
  total: number;
  percentageUsed: number;
  totalRemaining: number;
}): CreditsV2Level {
  if (!(total > 0)) return 0;
  if (totalRemaining < CREDITS_V2_EMPTY_BELOW) return 3;
  if (percentageUsed >= CREDITS_V2_DANGER_USED_PERCENT) return 2;
  if (
    percentageUsed >= CREDITS_V2_WARNING_USED_PERCENT ||
    totalRemaining < CREDITS_V2_LOW_REMAINING
  ) {
    return 1;
  }
  return 0;
}

/** Critical/empty first, then warning, then healthy; alphabetical within each group. */
export function compareCreditsV2(
  a: { name: string; level: number },
  b: { name: string; level: number }
): number {
  const group = (level: number) => (level >= 2 ? 0 : level === 1 ? 1 : 2);
  return (
    group(a.level) - group(b.level) ||
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

/** Severity pills of the modal filter; they are exclusive (radio). */
export type CreditsV2Severity = 'critical' | 'warning' | 'normal';

export interface CreditsV2Filter {
  /** null = every severity. */
  severity: CreditsV2Severity | null;
  /** Combines (AND) with the severity: only companies with Meta bonus left. */
  bonus: boolean;
  search: string;
}

export type CreditsV2FilterCounts = Record<CreditsV2Severity | 'bonus', number>;

interface CreditsV2Filterable {
  name: string;
  level: number;
  bonus: boolean;
}

/** "Crítico" also holds the empty companies (level 3), as the list order does. */
export function creditsV2Severity(level: number): CreditsV2Severity {
  if (level >= 2) return 'critical';
  if (level === 1) return 'warning';
  return 'normal';
}

/** Case and accent insensitive, so "sao" finds "São". */
function normalizeSearch(text: string): string {
  return (text ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function filterCreditsV2<T extends CreditsV2Filterable>(
  balances: T[],
  { severity, bonus, search }: CreditsV2Filter
): T[] {
  const term = normalizeSearch(search);
  return balances.filter(
    (balance) =>
      (!severity || creditsV2Severity(balance.level) === severity) &&
      (!bonus || balance.bonus) &&
      (!term || normalizeSearch(balance.name).includes(term))
  );
}

/** Pill counters: always over every company of the modal, regardless of the active filter. */
export function countCreditsV2Filters(balances: CreditsV2Filterable[]): CreditsV2FilterCounts {
  const counts: CreditsV2FilterCounts = { critical: 0, warning: 0, normal: 0, bonus: 0 };
  for (const balance of balances) {
    counts[creditsV2Severity(balance.level)]++;
    if (balance.bonus) counts.bonus++;
  }
  return counts;
}

export interface ChatFooterCreditsInput {
  monthTotalLimit?: number;
  totalRemaining: number;
  bonusBalance?: number;
}

/** Red footer rule: 90% or more of the current month limit used. */
export function isCreditsRunningOut(credits: ChatFooterCreditsInput | null | undefined): boolean {
  const limit = credits?.monthTotalLimit;
  if (!limit || limit <= 0) return false;

  const used = ((limit - credits.totalRemaining) / limit) * 100;
  return used >= CREDITS_V2_DANGER_USED_PERCENT;
}

export interface ChatFooterCreditsState {
  /** What the next message costs: FEP > Meta bonus > charged. */
  kind: 'fep' | 'bonus' | 'charged';
  runningOut: boolean;
  /** Below one credit: a free message still goes, but the next charged one is blocked. */
  exhausted: boolean;
}

export function chatFooterCreditsState(
  credits: ChatFooterCreditsInput | null | undefined,
  isFepActive: boolean
): ChatFooterCreditsState {
  const runningOut = isCreditsRunningOut(credits);
  const exhausted = !!credits && credits.totalRemaining < CREDITS_V2_EMPTY_BELOW;

  if (isFepActive) return { kind: 'fep', runningOut, exhausted };
  if ((credits?.bonusBalance ?? 0) > 0) return { kind: 'bonus', runningOut, exhausted };
  return { kind: 'charged', runningOut, exhausted };
}
